import { ChevronDown, Trash2, Unlink } from 'lucide-react';

import {
  DetailRow,
  IncomingLineAuditTrail,
  IncomingLineDetails,
} from '@/components/bank-incoming/IncomingLineDetails';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/components/ui/collapsible';
import { useBankIncomingLine } from '@/hooks/react-query/useBankIncoming';
import type { DrawdownPayment } from '@/types/drawdown';
import { formatCurrency, formatDate } from '@/utils/formatters';

import { settledFromTheFeed, type PendingUnlink } from './investorPayments';

/**
 * One payment in full - what the bank delivered, or what was typed by hand.
 * Folded away by the header so instalments read as a list.
 */
export function PaymentDetailsSection({
  payment,
  investorName,
  currency,
  fundId,
  canEdit,
  isRemoving,
  defaultOpen,
  onUnlink,
}: {
  payment: DrawdownPayment;
  investorName: string;
  /** The call's currency, so a payment made in another one is flagged. */
  currency: string;
  fundId: string;
  canEdit: boolean;
  isRemoving: boolean;
  defaultOpen: boolean;
  onUnlink: (pending: PendingUnlink) => void;
}) {
  const fromTheFeed = settledFromTheFeed(payment);

  const summary = (
    <div className="flex items-center gap-2 text-sm">
      <ChevronDown className="h-4 w-4 text-muted-foreground transition-transform group-data-[state=closed]:-rotate-90" />
      <span className="font-medium tabular-nums">
        {formatCurrency(payment.amount, { currency: payment.currency })}
      </span>
      {currency !== payment.currency && (
        <span className="text-xs text-amber-700 dark:text-amber-400">
          paid in {payment.currency}
        </span>
      )}
      <span className="text-muted-foreground">
        {formatDate(payment.value_date ?? payment.payment_date)}
      </span>
      <Badge variant="outline" className="font-normal">
        {fromTheFeed ? 'IMPORTED' : 'MANUAL'}
      </Badge>
    </div>
  );

  const details = fromTheFeed ? (
    <SettledFromTheFeed
      incomingId={payment.bank_incoming_id as string}
      fundId={fundId}
    />
  ) : (
    <RecordedByHand payment={payment} />
  );

  return (
    <div className="rounded-lg border" data-testid="payment">
      <Collapsible defaultOpen={defaultOpen}>
        <div className="flex items-center justify-between gap-2 bg-muted/40 px-4 py-2">
          <CollapsibleTrigger className="group flex-1 text-left">
            {summary}
          </CollapsibleTrigger>
          {canEdit && (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => onUnlink({ payment, investorName })}
              disabled={isRemoving}
              aria-label={
                fromTheFeed ? 'Unlink bank transaction' : 'Delete entry'
              }
            >
              {fromTheFeed ? (
                <Unlink className="h-4 w-4" />
              ) : (
                <Trash2 className="h-4 w-4" />
              )}
            </Button>
          )}
        </div>
        <CollapsibleContent>
          <div className="space-y-4 border-t px-4 py-3">{details}</div>
        </CollapsibleContent>
      </Collapsible>
    </div>
  );
}

/** The staged line this payment was settled from, read in full. */
function SettledFromTheFeed({
  incomingId,
  fundId,
}: {
  incomingId: string;
  fundId: string;
}) {
  const scope = { fundId };
  const { data: line, isLoading } = useBankIncomingLine(incomingId, scope);

  if (isLoading) {
    return (
      <p className="text-sm text-muted-foreground">
        Loading the bank transaction…
      </p>
    );
  }
  if (!line) {
    return (
      <p className="text-sm text-muted-foreground">
        The bank transaction behind this payment is no longer available.
      </p>
    );
  }

  return (
    <>
      <IncomingLineDetails line={line} />
      <IncomingLineAuditTrail incomingId={incomingId} scope={scope} />
    </>
  );
}

/** A payment nobody imported: only what was typed in records it. */
function RecordedByHand({ payment }: { payment: DrawdownPayment }) {
  const iban =
    typeof payment.wire_details?.counterparty_iban === 'string'
      ? payment.wire_details.counterparty_iban
      : null;

  return (
    <>
      <p className="text-sm text-muted-foreground">
        Recorded by hand. No bank transaction sits behind this entry, so what
        was typed in is the whole record of it.
      </p>
      <div className="space-y-1">
        <DetailRow
          label="Payment date"
          value={formatDate(payment.payment_date)}
        />
        <DetailRow
          label="Value date"
          value={payment.value_date ? formatDate(payment.value_date) : null}
        />
        <DetailRow label="Method" value={payment.payment_method} />
        <DetailRow label="Reference" value={payment.reference_number ?? null} />
        <DetailRow label="Counterparty IBAN" value={iban} />
        <DetailRow
          label="Bank statement line"
          value={payment.bank_statement_line ?? null}
        />
        <DetailRow
          label="Processing status"
          value={payment.processing_status}
        />
        <DetailRow label="Notes" value={payment.notes ?? null} />
        <DetailRow label="Added by" value={payment.recorded_by_name ?? null} />
        <DetailRow label="Added at" value={formatDate(payment.created_at)} />
      </div>
    </>
  );
}
